<template>
  <div class="customer-accounts-table">
    <v-data-table
      :items="data"
      :headers="headers"
      :loading="isLoading"
      :options.sync="pagination"
      :footer-props="footerProps"
      @update:options="updateTable"
      :items-per-page="paginationMeta.itemsPerPage"
      :server-items-length="paginationMeta.serverItemsLength"
      :no-data-text="$dictionary.app.account.index.noResults"
      :loading-text="$dictionary.app.common.accounts.loading"
    >
      <template #item.id="{ item }">
        <span class="cursor-pointer">{{ item.id }} </span>
      </template>
      <template #item.name="{ item }">
        <table-matrics-row
          is-link
          is-text-pointer
          :text="item.name"
          text-class="blue--text"
          :query="{ active_tab: 'profile' }"
          :navigate-to="`${$paths.accounts}/${item.id}`"
        />
      </template>
      <template #item.created_at="{ item }">
        <table-matrics-row :text="item.created_at" />
      </template>
      <template #item.status="{ item }">
        <status-chip :text="item.status" />
      </template>
      <template #item.actions="{ item }">
        <v-menu left bottom offset-y>
          <template #activator="{ on, attrs }">
            <v-btn icon v-on="on">
              <v-icon v-bind="attrs" class="text--secondary">
                mdi-dots-vertical
              </v-icon>
            </v-btn>
          </template>

          <v-list dense>
            <v-list-item @click="editAccount(item)">
              <v-icon class="mr-3 headline text--secondary">
                mdi-pencil
              </v-icon>
              <v-list-item-title class="primary--font body-2 pr-3">
                Edit Account
              </v-list-item-title>
            </v-list-item>

            <v-list-item @click="onDisableAccount(item)">
              <v-icon class="mr-3 headline text--secondary">
                mdi-delete
              </v-icon>
              <v-list-item-title class="primary--font body-2 pr-3">
                Disable Account
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <delete-record-dialog
      :title="disableAccount.title"
      :is-loading="isDisabling"
      :dialog="disableAccount.dialog"
      @close="onAccountCancel"
      @delete="disableAccountApi"
      :sub-title="disableAccount.subTitle"
      btn-text="Disable"
    />
  </div>
</template>

<script>
import { PaginationSortMixin, SharedUtilsMixin } from "@/mixins";
import { TableMatricsRow } from "../shared";
import { StatusChip, DeleteRecordDialog } from "@/components/shared";

import { updateAccount } from "@/services";
import { isResponseCode, nameReplacer } from "@/utils/common.utils";
import { ACCOUNT_STATUS_OPTIONS, RESPONSE_CODES } from "@/constants";

/**
 * Customer account table component to show the listing of all accounts of a customer
 */
export default {
  name: "CustomerAccountsTable",
  /**
   * ---------------- Mixins ------------------
   */
  mixins: [PaginationSortMixin, SharedUtilsMixin],
  /**
   * ---------------- Components ------------------
   */
  components: {
    StatusChip,
    TableMatricsRow,
    DeleteRecordDialog,
  },
  /**
   * ---------------- Props ------------------
   */
  props: {
    data: { type: Array, required: true },
    headers: { type: Array, required: true },
    isLoading: { type: Boolean, required: true, default: false },
    defaultSortOrder: { type: String },
    sortDesc: { type: Boolean, default: false },
  },
  /**
   * ---------------- Data attributes ------------------
   */
  data() {
    const { title, subTitle } = this.$dictionary.app.customers.disableAccount;
    return {
      disableAccount: {
        dialog: false,
        title,
        subTitle,
      },
      title,
      selectedAccount: {},
      isDisabling: false,
    };
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    /**
     * Edits the selected account
     * @todo Implement edit account logic in the function
     */
    editAccount({ id, customer_id }) {
      this.$router.push({
        name: "edit-account",
        params: { id, customerId: customer_id },
      });
    },
    /**
     * Update table list
     */
    updateTable() {
      !this.isLoading && this.getRecords();
    },
    /**
     * Disable account payload
     */
    accountPayload() {
      const [, { value: status }] = ACCOUNT_STATUS_OPTIONS;
      const { id: account_id } = this.selectedAccount;
      return { account_id, status };
    },
    /**
     * Disable customer account status
     */
    async disableAccountApi() {
      try {
        this.isDisabling = true;
        let message = this.errorMessage;

        const { status } = await updateAccount(this.accountPayload());

        // Shows success message
        if (isResponseCode(status, RESPONSE_CODES.success)) {
          let { successMsg } = this.$dictionary.app.customers.disableAccount;
          message = successMsg;
          this.getRecords();
        }

        this.setSnackbar({
          value: true,
          message,
          type: this.snackbarTypes.success,
        });
        this.onAccountCancel();
      } finally {
        this.isDisabling = false;
      }
    },
    onAccountCancel() {
      this.disableAccount.dialog = false;
      this.selectedAccount = null;
    },
    /**
     * Deletes the selected account
     */
    onDisableAccount(account) {
      this.disableAccount.dialog = true;
      this.disableAccount.title = nameReplacer(this.title, account.name);
      this.selectedAccount = account;
    },
  },
};
</script>
